import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import { TrlFormSectionSeparator, TrlPill } from '»components/atoms';
import { TrlTextField } from '»components/atoms/Inputs';
import {
  onboardingMainFormFieldsSelector,
  onboardingSlice,
} from '»components/Auth/Onboarding/redux';
import { TrlFormButtons } from '»components/molecules/TrlFormButtons';
import { isAureaActiveClient } from '»utils/clientsUtils';
import { useFieldPropertyVerification } from '»utils/customHooks';
import { isSubmitFormButtonDisabled } from '»utils/form';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { FormStyled } from '../../../../Onboarding.styles';
import { FormWrapper } from '../../../FormWrapper';
import {
  ElementContainer,
  ElementHeader,
  ElementLabel,
} from '../LegalPersonForms.styles';
import { partnersFormValidation } from './PartnersForm.yup';

export const PartnersForm = () => {
  const dispatch = useDispatch();

  const mainFormFields = useSelector(onboardingMainFormFieldsSelector);

  const firstPartner = mainFormFields.find((field) => field.name === 'partners')
    .data[0];

  const { verifyFieldProperty } = useFieldPropertyVerification(firstPartner);

  const handleSubmit = (values) => {
    dispatch(
      onboardingSlice.actions.setOnboardingCurrentStep({
        direction: 'forward',
      }),
    );
    dispatch(onboardingSlice.actions.setMainFormValues(values));
  };

  return (
    <FormWrapper
      handleSubmitForm={handleSubmit}
      formValidation={partnersFormValidation}
    >
      {({ isSubmitting, dirty, errors, values, setFieldValue }) => (
        <FormStyled className="formContainer narrowFields">
          <TrlFormSectionSeparator
            title={TrlFormattedMessagePlain({
              id: 'TRLT_UI-PARTNERS_SHAREHOLDERS',
            })}
            infoText={
              isAureaActiveClient
                ? TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PARTNERS_SHAREHOLDERS_HINT',
                  })
                : ''
            }
          />
          {values.partners.map((_, index) => (
            <ElementContainer key={index} className="elementContainer">
              <ElementHeader>
                <ElementLabel>
                  {`${TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PARTNER',
                  })} ${index + 1}`}
                </ElementLabel>

                {index !== 0 && values.partners.length === index + 1 && (
                  <TrlPill
                    label={TrlFormattedMessagePlain({ id: 'TRLT_UI-DELETE' })}
                    variant="delete"
                    onClick={() => {
                      setFieldValue('partners', values.partners.slice(0, -1));
                    }}
                  />
                )}
              </ElementHeader>
              <Box className="flexWrap">
                <TrlTextField
                  name={`partners[${index}].partnerName`}
                  type="text"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PARTNER_NAME',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PARTNER_NAME',
                  })}
                  id="partnerName"
                  isHidden={verifyFieldProperty({
                    fieldName: 'partnerName',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'partnerName',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlTextField
                  name={`partners[${index}].partnerNif`}
                  type="text"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PARTNER_TAX_ID',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PARTNER_TAX_ID',
                  })}
                  id="partnerNif"
                  isHidden={verifyFieldProperty({
                    fieldName: 'partnerNif',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'partnerNif',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlTextField
                  name={`partners[${index}].capitalPctg`}
                  type="text"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-CAPITAL_SHARE_PERCENTAGE',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-CAPITAL_SHARE_PERCENTAGE',
                  })}
                  id="capitalPctg"
                  isHidden={verifyFieldProperty({
                    fieldName: 'capitalPctg',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'capitalPctg',
                    fieldProperty: 'isRequired',
                  })}
                  inputProps={{
                    maxLength: 5,
                  }}
                />
              </Box>
            </ElementContainer>
          ))}

          <TrlPill
            label={TrlFormattedMessagePlain({
              id: 'TRLT_UI-ADD_PARTNER',
            })}
            variant="addItem"
            onClick={() => {
              setFieldValue('partners', [
                ...values.partners,
                Object.keys(values.partners[0]).reduce((acc, field) => {
                  acc[field] = '';
                  return acc;
                }, {}),
              ]);
            }}
          />

          <TrlFormButtons
            fixedAtBottom
            advanceButtonName="TRLT_UI-NEXT"
            isAdvanceButtonDisabled={isSubmitFormButtonDisabled({
              isSubmitting,
              dirty,
              errors,
              values: values.partners,
              areFiles: false,
            })}
          />
        </FormStyled>
      )}
    </FormWrapper>
  );
};
